import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import platformMessagesService from '../../services/platformMessagesService';
import {
  datetimeLocalToIso,
  isoToDatetimeLocal,
  PLATFORM_MESSAGE_TYPES,
  validatePlatformMessageForm,
} from '../../lib/platformMessagesUtils';

const emptyForm = () => ({
  title: '',
  body: '',
  messageType: 'marketing',
  countryId: '',
  startsAt: '',
  endsAt: '',
  isActive: true,
  priority: '0',
  linkUrl: '',
  linkLabel: '',
});

const selectClassName =
  'w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const PlatformMessageFormModal = ({
  isOpen,
  onClose,
  message,
  onSaved,
  countries = [],
  countriesLoading = false,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (message) {
      setForm({
        title: message.title || '',
        body: message.body || '',
        messageType: message.messageType || 'marketing',
        countryId: message.countryId || '',
        startsAt: isoToDatetimeLocal(message.startsAt),
        endsAt: isoToDatetimeLocal(message.endsAt),
        isActive: message.isActive !== false,
        priority: String(message.priority ?? 0),
        linkUrl: message.linkUrl || '',
        linkLabel: message.linkLabel || '',
      });
    } else {
      setForm(emptyForm());
    }
    setErrors({});
  }, [isOpen, message]);

  const typeOptions = PLATFORM_MESSAGE_TYPES.map((o) => ({
    value: o.value,
    label: t(`adminPlatformMessages.types.${o.value}`, { defaultValue: o.label }),
  }));

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const startsAt = datetimeLocalToIso(form.startsAt);
    const endsAt = datetimeLocalToIso(form.endsAt);
    const payload = {
      title: form.title,
      body: form.body,
      messageType: form.messageType,
      countryId: form.countryId || null,
      startsAt,
      endsAt,
      isActive: form.isActive,
      priority: form.priority,
      linkUrl: form.linkUrl,
      linkLabel: form.linkLabel,
      id: message?.id,
    };
    const validation = validatePlatformMessageForm(payload);
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    try {
      setSaving(true);
      if (message?.id) {
        await platformMessagesService.update(message.id, payload);
        toast.success(t('adminPlatformMessages.saveSuccess'));
      } else {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        await platformMessagesService.create(payload, user?.id);
        toast.success(t('adminPlatformMessages.createSuccess'));
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('adminPlatformMessages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-lg animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl font-semibold text-foreground">
            {message
              ? t('adminPlatformMessages.editTitle')
              : t('adminPlatformMessages.createTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="platform-msg-type" className="block text-sm font-medium text-foreground mb-2">
              {t('adminPlatformMessages.fields.type')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <select
              id="platform-msg-type"
              className={selectClassName}
              value={form.messageType}
              onChange={(e) => handleChange('messageType', e.target.value)}
              required
            >
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {errors.messageType ? (
              <p className="text-xs text-destructive mt-1">{errors.messageType}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="platform-msg-country" className="block text-sm font-medium text-foreground mb-2">
              {t('adminPlatformMessages.fields.country')}
            </label>
            <select
              id="platform-msg-country"
              className={selectClassName}
              value={form.countryId}
              onChange={(e) => handleChange('countryId', e.target.value)}
              disabled={countriesLoading}
            >
              <option value="">{t('adminPlatformMessages.allCountries')}</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.countryName}
                  {c.countryCode ? ` (${c.countryCode})` : ''}
                </option>
              ))}
            </select>
            {countriesLoading ? (
              <p className="text-xs text-muted-foreground mt-1">{t('common.loading')}</p>
            ) : countries.length === 0 ? (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                {t('adminPlatformMessages.noCountriesConfigured')}
              </p>
            ) : null}
          </div>

          <Input
            label={t('adminPlatformMessages.fields.title')}
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('adminPlatformMessages.fields.body')}
            </label>
            <textarea
              className="w-full min-h-[80px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.body}
              onChange={(e) => handleChange('body', e.target.value)}
              rows={3}
            />
            {errors.body ? (
              <p className="text-xs text-error mt-1">{errors.body}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('adminPlatformMessages.fields.startsAt')}
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => handleChange('startsAt', e.target.value)}
              error={errors.startsAt}
              required
            />
            <Input
              label={t('adminPlatformMessages.fields.endsAt')}
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => handleChange('endsAt', e.target.value)}
              error={errors.endsAt}
              required
            />
          </div>

          <Input
            label={t('adminPlatformMessages.fields.priority')}
            type="number"
            value={form.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
          />

          <Input
            label={t('adminPlatformMessages.fields.linkUrl')}
            type="url"
            value={form.linkUrl}
            onChange={(e) => handleChange('linkUrl', e.target.value)}
            error={errors.linkUrl}
            placeholder="https://"
          />

          <Input
            label={t('adminPlatformMessages.fields.linkLabel')}
            value={form.linkLabel}
            onChange={(e) => handleChange('linkLabel', e.target.value)}
          />

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="rounded border-border"
            />
            {t('adminPlatformMessages.fields.isActive')}
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformMessageFormModal;
